/**
 * Countdown Timer for Announcement Bar
 *
 * Two modes:
 *  - Fixed deadline: set data-deadline="YYYY-MM-DDTHH:MM:SS+10:00" on the element
 *    -> counts down to that exact instant and shows days (used for the EOFY sale end).
 *  - Daily (default, no attribute): counts down to 12:00 PM Brisbane time, rolling
 *    to the next day once noon passes (the original "order before noon" behaviour).
 */
class AnnouncementCountdown extends HTMLElement {
  constructor() {
    super();
    this.targetHour = 12; // 12:00 PM Brisbane time (daily mode)
    this.targetMinute = 0;
    this.intervalId = null;
    this.deadline = null; // Date | null (fixed-deadline mode)
  }

  connectedCallback() {
    const raw = this.getAttribute('data-deadline');
    if (raw) {
      const parsed = new Date(raw);
      if (!isNaN(parsed.getTime())) {
        this.deadline = parsed;
      }
    }
    this.render();
    this.startCountdown();
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  render() {
    this.innerHTML = `
      <span class="countdown-timer">
        <span class="countdown-time"></span>
      </span>
    `;
  }

  getBrisbaneTime() {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  }

  getDailyTarget() {
    const brisbaneNow = this.getBrisbaneTime();
    const target = new Date(brisbaneNow);
    target.setHours(this.targetHour, this.targetMinute, 0, 0);
    if (brisbaneNow >= target) {
      target.setDate(target.getDate() + 1);
    }
    return { now: brisbaneNow, target };
  }

  calculateTimeRemaining() {
    let diff;
    if (this.deadline) {
      // Absolute instant (the ISO string carries its own offset).
      diff = this.deadline.getTime() - Date.now();
    } else {
      const { now, target } = this.getDailyTarget();
      diff = target.getTime() - now.getTime();
    }

    if (diff <= 0) {
      return null;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  updateDisplay() {
    const timeRemaining = this.calculateTimeRemaining();
    const timeElement = this.querySelector('.countdown-time');

    if (!timeRemaining) {
      if (this.deadline) {
        // Sale ended — stop and clear.
        if (this.intervalId) clearInterval(this.intervalId);
        if (timeElement) timeElement.textContent = '';
        return;
      }
      // Daily mode: roll to the next day's target.
      this.startCountdown();
      return;
    }

    if (!timeElement) return;

    if (this.deadline) {
      // Human-friendly, coarse-to-fine: days while far out, then hours, then minutes.
      // No seconds — the bar already carries the end date; this is gentle urgency.
      const { days, hours, minutes } = timeRemaining;
      let phrase;
      if (days >= 2) phrase = `${days} DAYS LEFT`;
      else if (days === 1) phrase = `1 DAY LEFT`;
      else if (hours >= 1) phrase = `${hours} ${hours === 1 ? 'HOUR' : 'HOURS'} LEFT`;
      else if (minutes >= 1) phrase = `${minutes} ${minutes === 1 ? 'MIN' : 'MINS'} LEFT`;
      else phrase = `ENDING NOW`;
      timeElement.textContent = `· ${phrase}`;
    } else {
      const parts = [];
      if (timeRemaining.hours > 0) parts.push(`${timeRemaining.hours} hrs`);
      parts.push(`${timeRemaining.minutes} mins`);
      parts.push(`${timeRemaining.seconds} secs`);
      timeElement.textContent = parts.join(' : ');
    }
  }

  startCountdown() {
    this.updateDisplay();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(() => {
      this.updateDisplay();
    }, 1000);
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
